import {
  invalidateTaxPaymentSlipsQueries,
  useRegisterTaxPaymentSlipDownload,
  useUpdateTaxPaymentSlipPaid,
} from '@/api/accounting-dealer'
import { useCompany } from '@/context/companies'
import { capitalize, formatCurrency, formatDate, toast } from '@/helpers'
import { MonthsEnum, TaxPaymentSlip, TaxPaymentSlipTypeLabel } from '@/types'
import { Box, Card, FormControlLabel, Switch, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { GuideActionButton } from './guide-action-button'

function getTaxSlipDueDate(month: number, year: number) {
  return new Date(Date.UTC(month === 12 ? year + 1 : year, month % 12, 20))
}

type TaxPaymentSlipCardProps = {
  slip: TaxPaymentSlip
}

export function TaxPaymentSlipCard({ slip }: TaxPaymentSlipCardProps) {
  const { company } = useCompany()
  const queryClient = useQueryClient()

  const { mutate, isPending, variables } = useUpdateTaxPaymentSlipPaid({
    onSuccess: () => {
      invalidateTaxPaymentSlipsQueries(queryClient)
    },
    onError: () => {
      toast.error('Não foi possível atualizar o status de pagamento.')
    },
  })

  const { mutate: registerDownload } = useRegisterTaxPaymentSlipDownload({
    onError: () => {
      toast.error('Não foi possível registrar o download da guia.')
    },
  })

  const isUpdating = isPending && variables?.slip_key === slip.key
  const guideAmount = slip.document_amount ?? slip.amount
  const dueDate = slip.due_date
    ? slip.due_date
    : getTaxSlipDueDate(slip.month, slip.year)

  return (
    <Card sx={{ px: 2, py: 1.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography>
          {`${TaxPaymentSlipTypeLabel[slip.type]} - ${MonthsEnum[slip.month]}/${slip.year}`}
        </Typography>
        <Typography color={slip.paid ? 'success.main' : 'warning.main'}>
          {slip.paid ? 'Pago' : 'Pagamento pendente'}
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={0.5}
      >
        <Typography variant="body2">
          Vencimento: {capitalize(formatDate(dueDate))}
        </Typography>
        <Typography variant="h6">
          {formatCurrency(Number(guideAmount))}
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={1}
        gap={1}
      >
        <FormControlLabel
          control={
            <Switch
              checked={slip.paid}
              disabled={isUpdating}
              onChange={(_, checked) =>
                mutate({
                  company_key: company.key,
                  paid: checked,
                  slip_key: slip.key,
                })
              }
            />
          }
          label="Você já pagou?"
        />

        <GuideActionButton
          dueDate={dueDate}
          paid={slip.paid}
          recalculationRequested={slip.status === 'recalculation_requested'}
          url={slip.url}
          onDownload={() => {
            if (!slip.url) return

            registerDownload({
              company_key: company.key,
              slip_key: slip.key,
            })
            window.open(slip.url, '_blank', 'noopener, noreferrer')
          }}
        />
      </Box>
    </Card>
  )
}
